import React, { useState, useEffect } from 'react';
import {
    Bookmark,
    Upload,
    BrainCircuit,
    FileText,
    PenTool,
    Folder,
    Star,
    MoreVertical,
    MessageCircleQuestion,
    Book,
    Trash2,
    Download,
    X,
    ChevronLeft
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useToast } from '../hooks/use-toast';
import formatDate from '../utils/formatDate';
import CreateQuizModal from './library/CreateQuizModal';
import AIResultModal from './pdf/AIResultModal';

import '../styles/components/MyNotes.scss';

const EDIT_CATEGORIES = ["Book", "Note", "Exercise", "Quiz", "Lecture", "Course", "Material"];
const EDIT_VISIBILITY = ["Public", "Private", "Unlisted"];

const MyNotes = ({ collections, fetchCollections, activeFolderId, setActiveFolderId, onUploadClick }) => {
    const { token, user } = useAuthContext();
    const { toast } = useToast();
    const [materials, setMaterials] = useState({ uploaded: [], saved: [] });
    const [showQuizPicker, setShowQuizPicker] = useState(false);
    const [quizResult, setQuizResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('All');
    const [selectedIds, setSelectedIds] = useState([]);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [activeVaultMenu, setActiveVaultMenu] = useState(null); // ID of item whose menu is open
    const [activeCardMenu, setActiveCardMenu] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [editForm, setEditForm] = useState({ title: '', description: '', category: '', topic: '', visibility: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    // Any click that isn't on a menu closes whichever menu is open. The old
    // onMouseLeave closed it the moment the pointer travelled towards it.
    useEffect(() => {
        if (activeCardMenu === null && activeVaultMenu === null) return undefined;
        const closeMenus = (e) => {
            if (e.target.closest?.('.menu-container') || e.target.closest?.('.vault-menu-wrapper')) return;
            setActiveCardMenu(null);
            setActiveVaultMenu(null);
        };
        document.addEventListener('mousedown', closeMenus);
        return () => document.removeEventListener('mousedown', closeMenus);
    }, [activeCardMenu, activeVaultMenu]);

    // One edit per material per week, mirrored from the server so the button
    // can say so before the user types anything.
    const EDIT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
    const nextEditDate = (item) => {
        if (!item?.last_edited_at) return null;
        const next = new Date(new Date(item.last_edited_at).getTime() + EDIT_COOLDOWN_MS);
        return next > new Date() ? next : null;
    };

    const openEditor = (item) => {
        setEditForm({
            title: item.title || '',
            description: item.description || '',
            category: item.category || 'Note',
            topic: item.topic || '',
            visibility: item.visibility || 'Public',
        });
        setEditingItem(item);
    };

    const handleEditSave = async () => {
        if (!editingItem) return;
        const title = editForm.title.trim();
        if (!title) {
            toast({ title: "Error", description: "Title cannot be empty.", variant: "destructive" });
            return;
        }
        setSavingEdit(true);
        try {
            const res = await fetch(`/api/materials/${editingItem.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    title,
                    description: editForm.description.trim(),
                    category: editForm.category,
                    topic: editForm.topic.trim(),
                    visibility: editForm.visibility,
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save changes.');
            setEditingItem(null);
            fetchMaterials();
            toast({ title: "Saved", description: "Your changes are live. You can edit this material again in a week." });
        } catch (err) {
            toast({ title: "Error", description: err.message, variant: "destructive" });
        } finally {
            setSavingEdit(false);
        }
    };

    useEffect(() => {
        fetchMaterials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMaterials = async () => {
        try {
            const res = await fetch('/api/materials/my-books', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setMaterials(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const toggleSave = async (pdfId) => {
        try {
            const res = await fetch('/api/materials/toggle-save', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ pdfId })
            });

            if (res.ok) {
                fetchMaterials(); // Refresh lists
            }
        } catch (err) {
            console.error('Failed to toggle save:', err);
        }
    };

    const handleSelectAll = (e, listPath) => {
        let list = [];
        if (listPath === 'all_folder') {
            const activeFolder = collections.find(c => c.id === activeFolderId);
            const allItems = [...materials.uploaded, ...materials.saved];
            // deduplicate
            const uniqueItems = Array.from(new Map(allItems.map(item => [item.id, item])).values());
            list = uniqueItems.filter(item => activeFolder?.pdf_ids?.includes(item.id));
        } else {
            list = getFilteredList(listPath);
        }

        if (e.target.checked) {
            const newIds = list.map(item => item.id);
            // Combine without duplicate
            setSelectedIds(prev => Array.from(new Set([...prev, ...newIds])));
        } else {
            const listIds = list.map(item => item.id);
            setSelectedIds(prev => prev.filter(id => !listIds.includes(id)));
        }
    };

    const isAllVisibleSelected = (listPath) => {
        const list = listPath === 'all_folder'
            ? getFilteredList('all_folder')
            : getFilteredList(listPath);
        if (list.length === 0) return false;
        return list.every(item => selectedIds.includes(item.id));
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleMoveToCollection = async (collectionName) => {
        try {
            const res = await fetch('/api/folders/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: collectionName,
                    pdfIds: selectedIds
                })
            });

            if (res.ok) {
                setShowMoveModal(false);
                setSelectedIds([]);
                // alert(`Moved ${selectedIds.length} items to ${collectionName}`);
                fetchCollections(); // refresh collections
            } else {
                console.error("Failed to move items to collection.");
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleRemoveFromFolder = async (pdfId) => {
        if (!activeFolderId) return;
        try {
            const res = await fetch(`/api/folders/${activeFolderId}/remove`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ pdfId })
            });
            if (res.ok) {
                fetchCollections();
                setActiveVaultMenu(null);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (window.confirm(`Are you sure you want to delete the ${selectedIds.length} selected materials?`)) {
            try {
                const promises = selectedIds.map(id =>
                    fetch(`/api/materials/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                );
                const results = await Promise.all(promises);
                const failed = results.filter(r => !r.ok);
                
                setSelectedIds([]);
                fetchMaterials();
                
                if (failed.length === 0) {
                    toast({
                        title: "Success",
                        description: "Selected materials deleted successfully.",
                    });
                } else {
                    toast({
                        title: "Warning",
                        description: `Deleted some materials, but ${failed.length} failed.`,
                        variant: "warning"
                    });
                }
            } catch (err) {
                console.error(err);
                toast({
                    title: "Error",
                    description: "Failed to delete some materials.",
                    variant: "destructive"
                });
            }
        }
    };

    const filters = [
        { id: 'All', icon: <Book size={14} /> },
        { id: 'Uploaded', icon: <Upload size={14} /> },
        { id: 'Saved', icon: <Bookmark size={14} /> },
        { id: 'Quizzes', icon: <BrainCircuit size={14} /> },
        { id: 'Notes', icon: <FileText size={14} /> },
        { id: 'Exercises', icon: <PenTool size={14} /> },
    ];

    const getFilteredList = (type) => { // type: 'uploaded' | 'saved' | 'all_folder'
        let list = materials[type] || [];
        if (type === 'all_folder') {
            const allItems = [...materials.uploaded, ...materials.saved];
            const uniqueItems = Array.from(new Map(allItems.map(item => [item.id, item])).values());
            list = uniqueItems;
        }

        if (activeFolderId) {
            const activeFolder = collections.find(c => c.id === activeFolderId);
            if (activeFolder && activeFolder.pdf_ids) {
                list = list.filter(item => activeFolder.pdf_ids.includes(item.id));
            } else if (activeFolderId) {
                return [];
            }
        }

        if (activeFilter === 'All') return list;
        if (activeFilter === 'Uploaded' && type === 'uploaded') return list;
        if (activeFilter === 'Saved' && type === 'saved') return list;

        // Hide types if a specific list filter is chosen
        if (['Uploaded', 'Saved'].includes(activeFilter) && activeFilter.toLowerCase() !== type) return [];

        // Category filtering (Quizzes, Notes, Exercises)
        const catMap = { 'Quizzes': 'Quiz', 'Notes': 'Note', 'Exercises': 'Exercise', 'Books': 'Book' };
        if (catMap[activeFilter]) {
            return list.filter(item => item.category === catMap[activeFilter]);
        }

        return list;
    };

    const quizzesCreated = materials.uploaded.filter(m => m.category === 'Quiz').length;
    const notesCreated = materials.uploaded.filter(m => m.category === 'Note').length;

    const FolderVaultView = ({ items }) => {
        return (
            <div className="folder-vault-container">
                <div className="vault-table-header">
                    <div className="col-check">
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={isAllVisibleSelected('all_folder')}
                                onChange={(e) => handleSelectAll(e, 'all_folder')}
                            />
                            <span className="checkmark"></span>
                        </label>
                    </div>
                    <div className="col-name">Name</div>
                    <div className="col-info">Category & Date</div>
                    <div className="col-actions"></div>
                </div>
                <div className="vault-rows">
                    {items.map(item => {
                        const isSelected = selectedIds.includes(item.id);
                        const isMenuOpen = activeVaultMenu === item.id;
                        return (
                            <div key={item.id} className={`vault-row ${isSelected ? 'selected' : ''}`} onClick={() => toggleSelect(item.id)}>
                                <div className="col-check" onClick={(e) => e.stopPropagation()}>
                                    <label className="checkbox-container">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(item.id)} />
                                        <span className="checkmark"></span>
                                    </label>
                                </div>
                                <div className="col-name">
                                    <div className="file-icon">
                                        {item.category === 'Book' && <Book size={18} />}
                                        {item.category === 'Note' && <FileText size={18} />}
                                        {item.category === 'Exercise' && <MessageCircleQuestion size={18} />}
                                        {item.category === 'Quiz' && <BrainCircuit size={18} />}
                                    </div>
                                    <span>{item.title}</span>
                                </div>
                                <div className="col-info">
                                    <span className={`badge ${item.category}`}>{item.category}</span>
                                    <span className="date-added">Added {formatDate(item.createdAt, user?.date_format)}</span>
                                </div>
                                <div className="col-actions" onClick={(e) => e.stopPropagation()}>
                                    <div className="vault-menu-wrapper">
                                        <button className="row-action-btn" onClick={() => { setActiveCardMenu(null); setActiveVaultMenu(isMenuOpen ? null : item.id); }}>
                                            <MoreVertical size={16} />
                                        </button>
                                        {isMenuOpen && (
                                            <div className="vault-dropdown">
                                                <button onClick={() => { 
                                                    setActiveVaultMenu(null); 
                                                    const lang = window.location.pathname.split('/')[1] || 'en';
                                                    window.location.href = `/${lang}/material/${item.id}`;
                                                }}>View Details</button>
                                                <button onClick={() => { setActiveVaultMenu(null); openEditor(item); }}>Edit</button>
                                                <button onClick={() => handleRemoveFromFolder(item.id)} className="danger">Remove from Folder</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const MaterialCard = ({ item, isSaved }) => {
        const isSelected = selectedIds.includes(item.id);
        const showMenu = activeCardMenu === item.id;
        const setShowMenu = (open) => setActiveCardMenu(open ? item.id : null);
        const lockedUntil = nextEditDate(item);

        // Values strictly from the DB
        const pages = item.pages || 0;
        const readTime = item.readTime || 0;
        const rating = item.rating || 0;

        const handleMenuToggle = (e) => {
            e.stopPropagation();
            setActiveVaultMenu(null);
            setShowMenu(!showMenu);
        };

        const handleDelete = async (e) => {
            e.stopPropagation();
            setShowMenu(false);
            if (window.confirm("Are you sure you want to delete this material?")) {
                try {
                    const res = await fetch(`/api/materials/${item.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        fetchMaterials();
                        toast({
                            title: "Success",
                            description: "Material deleted successfully."
                        });
                    } else {
                        const data = await res.json();
                        toast({
                            title: "Error",
                            description: data.error || 'Delete failed',
                            variant: "destructive"
                        });
                    }
                } catch (err) {
                    console.error(err);
                    toast({
                        title: "Error",
                        description: "Network error during delete",
                        variant: "destructive"
                    });
                }
            }
        };

        const handleShare = async (e) => {
            e.stopPropagation();
            setShowMenu(false);
            const lang = window.location.pathname.split('/')[1] || 'en';
            const url = `${window.location.origin}/${lang}/material/${item.id}`;
            try {
                await navigator.clipboard.writeText(url);
                toast({ title: "Link copied", description: "Anyone with the link can open this material." });
            } catch {
                toast({ title: "Could not copy", description: url, variant: "destructive" });
            }
        };

        const handleCardClick = (e) => {
            // If clicking a button or checkbox, don't navigate
            if (e.target.closest('button') || e.target.closest('input')) return;
            
            const lang = window.location.pathname.split('/')[1] || 'en';
            window.location.href = `/${lang}/material/${item.id}`;
        };

        return (
            <div className={`material-card ${isSelected ? 'selected' : ''}`} onClick={handleCardClick}>
                <div className="card-preview">
                    <div className="selection-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                        <label className="checkbox-container">
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelect(item.id)}
                            />
                            <span className="checkmark"></span>
                        </label>
                    </div>
                    {/* Placeholder content mapping to category */}
                    {item.category === 'Book' && <Book size={48} className="icon-placeholder mt-4" />}
                    {item.category === 'Note' && <FileText size={48} className="icon-placeholder mt-4" />}
                    {item.category === 'Exercise' && <MessageCircleQuestion size={48} className="icon-placeholder mt-4" />}
                    {item.category === 'Quiz' && <BrainCircuit size={48} className="icon-placeholder mt-4" />}

                    <span className={`tag ${item.category || 'Note'}`}>{item.category || 'Note'}</span>
                    <div className="action-btns">
                        <button
                            type="button"
                            title={isSaved ? "Saved" : "Save"}
                            className={`bookmark-btn ${isSaved ? 'active' : ''}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSave(item.id); }}
                        >
                            <Bookmark size={16} fill={isSaved ? "currentColor" : "none"} />
                        </button>
                    </div>
                </div>

                <div className="card-content">
                    <div className="card-title-row">
                        <h4>{item.title}</h4>
                        <div className="menu-container">
                            <button className="more-opts" onClick={handleMenuToggle} title="More options">
                                <MoreVertical size={16} />
                            </button>
                            {showMenu && (
                                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setShowMenu(false); openEditor(item); }}
                                        disabled={Boolean(lockedUntil)}
                                        title={lockedUntil ? `You can edit again on ${lockedUntil.toLocaleDateString('en-GB')}` : 'Edit details'}
                                    >
                                        {lockedUntil ? `Edit — locked till ${lockedUntil.toLocaleDateString('en-GB')}` : 'Edit'}
                                    </button>
                                    <button onClick={handleShare}>Copy link</button>
                                    <button className="danger" onClick={handleDelete}>Delete</button>
                                </div>
                            )}
                        </div>
                    </div>
                    {item.description && <p className="card-desc">{item.description}</p>}

                    <div className="card-meta-inline">
                        <span>You</span>
                        <span className="separator">•</span>
                        <span>{item.university || 'Baku State University'}</span>
                        <span className="separator">•</span>
                        <span>{item.relative_specialities || 'Chemical Engineering'}</span>
                    </div>

                    <div className="card-footer">
                        <div className="footer-left">
                            <span>{readTime} min read</span>
                            <span>{pages} pages</span>
                        </div>
                        <div className="rating-box">
                            <Star size={14} />
                            <span>{rating}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-[1440px] mx-auto px-6 py-6 font-sans">
            <div className="my-books-dashboard">
            {selectedIds.length > 0 && (
                <div className="selection-toolbar">
                    <div className="toolbar-left">
                        <label className="select-all-checkbox">
                            <input
                                type="checkbox"
                                checked={isAllVisibleSelected(activeFilter)}
                                onChange={(e) => handleSelectAll(e, activeFilter)}
                            />
                            <span className="checkmark"></span>
                            {selectedIds.length} Selected
                        </label>
                        <button className="toolbar-btn" onClick={() => setShowMoveModal(true)}>
                            <Folder size={16} /> Move to Collection
                        </button>
                        <button className="toolbar-btn" onClick={() => toast({ title: "Coming Soon", description: "Exporting materials is under development." })}>
                            <Download size={16} /> Export
                        </button>
                        <button className="toolbar-btn delete-btn" onClick={handleBulkDelete}>
                            <Trash2 size={16} /> Delete
                        </button>
                    </div>
                    <button className="close-toolbar-btn" onClick={() => setSelectedIds([])}>
                        <X size={18} />
                    </button>
                </div>
            )}

            <div className="my-books-header">
                <div className="header-left">
                    <div className="title-with-back">
                        {activeFolderId && (
                            <button className="back-to-all-btn" onClick={() => setActiveFolderId(null)} title="Back to My Notes">
                                <ChevronLeft size={20} />
                            </button>
                        )}
                        <h1 className="text-[28px] font-display font-bold text-foreground">
                            {activeFolderId ? collections.find(c => c.id === activeFolderId)?.name || 'Folder' : "My Notes"}
                        </h1>
                    </div>
                    <p>{activeFolderId ? "Viewing items inside this collection" : "Manage your uploaded materials, saved resources, and created content"}</p>
                </div>
                <div className="header-actions">
                    <button
                        className="header-btn"
                        onClick={() => onUploadClick && onUploadClick()}
                    >
                        <Upload size={14} /> Upload
                    </button>
                    <button
                        className="qna-btn"
                        onClick={() => toast({ title: "Coming Soon", description: "Questions & Answers is under development." })}
                    >
                        <FileText size={16} /> Questions & Answers
                    </button>
                    <button
                        className="qna-btn"
                        onClick={() => setShowQuizPicker(true)}
                    >
                        <BrainCircuit size={16} /> Create Quiz
                    </button>
                </div>
            </div>

            {showQuizPicker && (
                <CreateQuizModal
                    token={token}
                    onClose={() => setShowQuizPicker(false)}
                    onGenerated={(data) => { setShowQuizPicker(false); setQuizResult(data); }}
                />
            )}
            <AIResultModal
                isOpen={!!quizResult}
                onClose={() => setQuizResult(null)}
                loading={false}
                error={null}
                title="Quiz"
                textContent=""
                questions={quizResult?.questions}
                materialTitle={quizResult?.materialTitle}
            />

            {!activeFolderId && (
                <>
                    <div className="stats-summary-grid">
                        <div className="summary-card">
                            <div className="icon-box blue"><Bookmark size={20} /></div>
                            <div className="info">
                                <h3>{materials.saved.length || 0}</h3>
                                <span>Saved Resources</span>
                            </div>
                        </div>
                        <div className="summary-card">
                            <div className="icon-box green"><Upload size={20} /></div>
                            <div className="info">
                                <h3>{materials.uploaded.length || 0}</h3>
                                <span>Uploaded Materials</span>
                            </div>
                        </div>
                        <div className="summary-card">
                            <div className="icon-box purple"><BrainCircuit size={20} /></div>
                            <div className="info">
                                <h3>{quizzesCreated}</h3>
                                <span>Quizzes Created</span>
                            </div>
                        </div>
                        <div className="summary-card">
                            <div className="icon-box orange"><FileText size={20} /></div>
                            <div className="info">
                                <h3>{notesCreated}</h3>
                                <span>Notes Created</span>
                            </div>
                        </div>
                    </div>

                    <div className="filter-tabs">
                        {filters.map(f => (
                            <button
                                key={f.id}
                                className={`filter-btn ${activeFilter === f.id ? 'active' : ''}`}
                                onClick={() => setActiveFilter(f.id)}
                            >
                                {f.icon} {f.id}
                            </button>
                        ))}
                    </div>
                </>
            )}

            {loading ? (
                <div className="books-grid">
                    {[1, 2, 3, 4].map(i => <div key={i} className="skeleton"></div>)}
                </div>
            ) : activeFolderId ? (
                <div className="section-container vault-section">
                    <FolderVaultView items={getFilteredList('all_folder')} />
                </div>
            ) : (
                <>
                    {/* Saved Section */}
                    {(activeFilter === 'All' || activeFilter === 'Saved') && getFilteredList('saved').length > 0 && (
                        <div className="section-container">
                            <div className="section-header">
                                <h2>Saved Materials <span className="count-badge">{getFilteredList('saved').length}</span></h2>
                                <label className="select-all-label">
                                    <span className="label-text">Select all</span>
                                    <label className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            checked={isAllVisibleSelected('saved')}
                                            onChange={(e) => handleSelectAll(e, 'saved')}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                </label>
                            </div>
                            <div className="books-grid">
                                {getFilteredList('saved').map(item => (
                                    <MaterialCard key={item.id} item={item} isSaved={true} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Uploaded Section */}
                    {(activeFilter === 'All' || activeFilter === 'Uploaded' || ['Quizzes', 'Notes', 'Exercises'].includes(activeFilter)) && getFilteredList('uploaded').length > 0 && (
                        <div className="section-container">
                            <div className="section-header">
                                <h2>Uploaded <span className="count-badge">{getFilteredList('uploaded').length}</span></h2>
                                <label className="select-all-label">
                                    <span className="label-text">Select all</span>
                                    <label className="checkbox-container">
                                        <input
                                            type="checkbox"
                                            checked={isAllVisibleSelected('uploaded')}
                                            onChange={(e) => handleSelectAll(e, 'uploaded')}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                </label>
                            </div>
                            <div className="books-grid">
                                {getFilteredList('uploaded').map(item => (
                                    <MaterialCard key={item.id} item={item} isSaved={materials.saved.some(s => s.id === item.id)} />
                                ))}
                            </div>
                        </div>
                    )}

                    {getFilteredList('uploaded').length === 0 && getFilteredList('saved').length === 0 && (
                        <div className="empty-state-message" style={{ textAlign: 'center', padding: '40px', color: 'hsl(var(--muted-foreground))' }}>
                            No materials found matching this filter.
                        </div>
                    )}
                </>
            )}

            {editingItem && (
                <div className="move-modal-overlay" onClick={(e) => { if (e.target.className === 'move-modal-overlay') setEditingItem(null); }}>
                    <div className="move-modal">
                        <div className="modal-header">
                            <h3><FileText size={18} /> Edit Material</h3>
                            <button className="close-btn" onClick={() => setEditingItem(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <label>Title</label>
                            <input
                                type="text"
                                value={editForm.title}
                                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                                className="dark-input"
                            />

                            <label style={{ marginTop: '12px', display: 'block' }}>Description</label>
                            <textarea
                                value={editForm.description}
                                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                className="dark-input"
                                rows={4}
                                style={{ width: '100%', resize: 'vertical' }}
                            />

                            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                                <div style={{ flex: 1 }}>
                                    <label>Category</label>
                                    <select
                                        value={editForm.category}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                                        className="dark-input"
                                        style={{ width: '100%' }}
                                    >
                                        {EDIT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Visibility</label>
                                    <select
                                        value={editForm.visibility}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, visibility: e.target.value }))}
                                        className="dark-input"
                                        style={{ width: '100%' }}
                                    >
                                        {EDIT_VISIBILITY.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                </div>
                            </div>

                            <label style={{ marginTop: '12px', display: 'block' }}>Subject / topic</label>
                            <input
                                type="text"
                                value={editForm.topic}
                                onChange={(e) => setEditForm(prev => ({ ...prev, topic: e.target.value }))}
                                className="dark-input"
                                placeholder="e.g. Organic Chemistry"
                            />

                            <p className="hint-text" style={{ marginTop: '14px' }}>
                                A material can be edited once a week — including its cover. Saving nothing new
                                costs nothing, so take your time getting it right.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setEditingItem(null)}>Cancel</button>
                            <button className="submit-btn" disabled={savingEdit} onClick={handleEditSave}>
                                {savingEdit ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showMoveModal && (
                <div className="move-modal-overlay" onClick={(e) => { if (e.target.className === 'move-modal-overlay') setShowMoveModal(false); }}>
                    <div className="move-modal">
                        <div className="modal-header">
                            <h3><Folder size={18} /> Move to Collection</h3>
                            <button className="close-btn" onClick={() => setShowMoveModal(false)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <label>Collection Name</label>
                            <input
                                type="text"
                                placeholder="e.g., Organic Chemistry Prep"
                                className="dark-input"
                                id="collection-name-input"
                            />
                            <p className="hint-text">Create a new collection or type an existing name to move {selectedIds.length} items.</p>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => setShowMoveModal(false)}>Cancel</button>
                            <button className="submit-btn" onClick={() => {
                                const name = document.getElementById('collection-name-input').value;
                                if (name) handleMoveToCollection(name);
                            }}>Move Items</button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default MyNotes;
